import { FC, HTMLAttributes, useState, useRef } from 'react';
import { IProductContext, useProductContext } from './useProductContext';
import Iconfont from '@base/lib/Iconfont';
import { Button, ButtonProps } from '@base/lib/Button';
import { useStore } from 'base/store';
import { transImageSrc } from 'base/utils/trans-path';
import { trackAddToCart } from 'base/utils/tracker';
import { postAddCart } from '@base/product/services';
import Message from '@base/lib/Message';
import { commonErrorHandler } from 'base/utils/api-callback';
import { isSoldOut } from 'mod/product-list/utils';
import { toRecord } from 'base/utils/data';
import { loadImage } from 'ava-util/dist/image';
import { IProduct } from 'mod/product/interface';
import delay from 'base/utils/delay';

export type IProductAddCartProps = HTMLAttributes<HTMLDivElement> & {
    render?: (addCartBtnProps: ButtonProps, result?: keyof typeof btnTypeConfig) => JSX.Element
};

export const btnTypeConfig = {
    success: {
        icon: 'icon-select',
        ariaText: 'add success, add again'
    },
    fail: {
        icon: 'icon-x',
        ariaText: 'add fail, try again'
    },
    default: {
        icon: 'icon-a-addtocart',
        ariaText: 'add to cart'
    },
} as const

type IMainProps = IProductAddCartProps & {
    product: IProduct;
    addCart: IProductContext['addCart'];
    render?: (addCartBtnProps: ButtonProps, result?: keyof typeof btnTypeConfig) => JSX.Element
}

const Main: FC<IMainProps> = props => {
    const { product, addCart, render } = props;
    const { qty = 1, successCallback, ...rest } = toRecord(addCart);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [result, setResult] = useState<keyof typeof btnTypeConfig>('default')
    const { miniCartStore } = useStore();
    const btnRef = useRef<HTMLButtonElement>(null);

    const handleClick = async () => {
        const btnEl = btnRef.current as HTMLButtonElement;
        btnEl.blur();
        const imgEl = btnEl.closest('.js-product')?.querySelector('.js-picture img') as HTMLImageElement;
        const animatePic = imgEl ? imgEl.currentSrc : transImageSrc(product.pic.url, 200, 1);
        //预加载图片
        if (!imgEl) {
            loadImage(animatePic);
        }

        trackAddToCart([{ ...product, num: 1 }]);

        setSubmitting(true);
        try {
            const res = await postAddCart([{
                productVariantId: product.variantId,
                num: qty
            },]);
            if (!res.addFailProductName || res.addFailProductName.length === 0) {
                miniCartStore.afterAddCart(qty, [animatePic]);
                successCallback?.();
                setResult('success');
            } else {
                Message.error(`${res.addFailProductName[0]} is temporarily unavailable.`);
                setResult('fail');
            }
        } catch (e) {
            commonErrorHandler(e);
            setResult('fail');
        }
        setSubmitting(false);
        await delay(500);
        setResult('default');
    }

    const btnProps = {
        ['aria-label']: btnTypeConfig[result].ariaText,
        ref: btnRef,
        className: 'g-add-cart',
        loading: submitting,
        disabled: isSoldOut(product),
        onClick: async (e) => {
            e.stopPropagation();
            await handleClick();
        },
        type: 'text',
        icon: <Iconfont className={btnTypeConfig[result].icon || btnTypeConfig.default.icon}/>,
        ...rest
    } as unknown as ButtonProps

    return render ? render(btnProps, result) : (
        <Button
            {...btnProps}
        />
    )
};

export const ProductAddCart: FC<IProductAddCartProps> = props => {
    const { product, addCart } = useProductContext();
    return addCart === false || product.disableActions.includes('addToCart') ? null : (
        <Main
            product={product}
            addCart={toRecord(addCart)}
            {...props}
        />
    )
};